/**
 * Unified status pill for invoices, bills, payments, etc.
 *
 * Why a shared component (vs the inline `StatusTag` in InvoicesPage)? Two
 * reasons:
 *   - Consistency: pills on Invoices, Bills, Customer detail, etc. all
 *     looked slightly different — easy drift, hard for the user to learn.
 *   - Overdue detection: an invoice is `sent` in the DB but "overdue" if
 *     due_date has passed and it's not fully paid. Computing that in one
 *     place stops every list page reinventing the wheel.
 */

export type DocStatus =
  // Shared
  | "draft" | "void"
  // Invoice
  | "sent" | "partially_paid" | "paid" | "overdue"
  // Bill
  | "pending_approval" | "approved" | "rejected"
  // Subscription
  | "active" | "past_due" | "cancelled" | "ended";

interface Palette { bg: string; fg: string; dot: string }

const PALETTES: Record<string, Palette> = {
  draft:            { bg: "var(--paper-2)",     fg: "var(--ink-3)",       dot: "var(--ink-4)" },
  void:             { bg: "var(--paper-3)",     fg: "var(--ink-4)",       dot: "var(--ink-4)" },
  sent:             { bg: "var(--gold-soft)",   fg: "#6E5320",            dot: "#B89252" },
  partially_paid:   { bg: "var(--accent-soft)", fg: "var(--accent-deep)", dot: "var(--accent)" },
  paid:             { bg: "var(--accent)",      fg: "white",              dot: "white" },
  overdue:          { bg: "var(--rose-soft)",   fg: "var(--rose)",        dot: "var(--rose)" },
  pending_approval: { bg: "var(--gold-soft)",   fg: "#6E5320",            dot: "#B89252" },
  approved:         { bg: "var(--accent-soft)", fg: "var(--accent-deep)", dot: "var(--accent)" },
  rejected:         { bg: "var(--rose-soft)",   fg: "var(--rose)",        dot: "var(--rose)" },
  active:           { bg: "var(--accent-soft)", fg: "var(--accent-deep)", dot: "var(--accent)" },
  past_due:         { bg: "var(--rose-soft)",   fg: "var(--rose)",        dot: "var(--rose)" },
  cancelled:        { bg: "var(--paper-3)",     fg: "var(--ink-4)",       dot: "var(--ink-4)" },
  ended:            { bg: "var(--paper-3)",     fg: "var(--ink-4)",       dot: "var(--ink-4)" },
};

const LABELS: Record<string, string> = {
  draft: "Draft",
  void: "Void",
  sent: "Sent",
  partially_paid: "Partial",
  paid: "Paid",
  overdue: "Overdue",
  pending_approval: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  active: "Active",
  past_due: "Past due",
  cancelled: "Cancelled",
  ended: "Ended",
};

/**
 * Decide the displayed status given the document's stored status + dates +
 * paid balance. Centralised so list rows and the detail page agree.
 *
 * Rule: a `sent` or `partially_paid` invoice/bill becomes `overdue` once
 * due_date is in the past (today's date strictly past due_date) AND it's
 * not yet fully paid. `paid` / `void` / `draft` never become overdue.
 */
export function effectiveStatus(
  status: string,
  opts: { dueDate?: string | null; paidCents?: string | number | bigint; totalCents?: string | number | bigint } = {},
): string {
  if (status === "paid" || status === "void" || status === "draft") return status;
  if (!opts.dueDate) return status;
  const due = new Date(opts.dueDate);
  if (Number.isNaN(due.getTime())) return status;
  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  if (due.getTime() >= todayStart.getTime()) return status;
  // Past due — only show as overdue if there's still a balance.
  try {
    const paid = BigInt(String(opts.paidCents ?? "0"));
    const total = BigInt(String(opts.totalCents ?? "0"));
    if (paid >= total && total > 0n) return status;
  } catch { /* fall through */ }
  if (status === "sent" || status === "partially_paid" || status === "approved") return "overdue";
  return status;
}

export function StatusPill({
  status,
  dueDate,
  paidCents,
  totalCents,
  size = "sm",
}: {
  status: string;
  dueDate?: string | null;
  paidCents?: string | number | bigint;
  totalCents?: string | number | bigint;
  size?: "sm" | "md";
}) {
  const effective = effectiveStatus(status, { dueDate, paidCents, totalCents });
  const p = PALETTES[effective] ?? PALETTES.draft;
  const label = LABELS[effective] ?? effective.replace(/_/g, " ");
  const padding = size === "md" ? "5px 12px" : "3px 10px";
  const fontSize = size === "md" ? 12 : 11;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding,
        borderRadius: 100,
        fontSize,
        fontWeight: 500,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        background: p.bg,
        color: p.fg,
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: p.dot,
          // Pulse for overdue / past_due / rejected so they catch the eye.
          animation: effective === "overdue" || effective === "past_due" || effective === "rejected"
            ? "statusPulse 1.8s ease-in-out infinite"
            : undefined,
        }}
      />
      {label}
    </span>
  );
}

/** Inject the pulse keyframes once on first import. */
function injectKeyframes() {
  if (typeof document === "undefined") return;
  const id = "statuspill-keyframes";
  if (document.getElementById(id)) return;
  const style = document.createElement("style");
  style.id = id;
  style.textContent = `
    @keyframes statusPulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.55; transform: scale(0.85); }
    }
  `;
  document.head.appendChild(style);
}
injectKeyframes();
